import {
    action,
    computed,
    makeAutoObservable,
    reaction,
    runInAction,
} from "mobx";
import moment, { Moment, unitOfTime } from "moment";
import { Menu, MenuCategory } from "../../../../../services/menu/models";
import { getMenus } from "../../../../../services/menu";
import {
    OrdersTotalPriceForCurrencyId,
    DataForGraph,
    StatsPeriodDataType,
    Period,
} from "../../../../../services/stats/models";
import { getStats } from "../../../../../services/stats";
import periodOptions, { IPeriodOption } from "../components/periodsTab/data";
import GraphFormsStore from "./graphforms";


export enum SelectMode {
    template,
    custom,
}

class PageStore {
    formsStore = new GraphFormsStore();
    isLoaded: boolean = false;
    menusList: Menu[] = [];
    currentMenu?: Menu;
    currentMenuCategory?: MenuCategory;
    from: Moment = periodOptions[0].from;
    to: Moment = periodOptions[0].to;
    generalStats?: OrdersTotalPriceForCurrencyId;
    isReady: boolean = false;
    period: IPeriodOption = periodOptions[0];
    mode: SelectMode = SelectMode.template;
    dataForGraph: DataForGraph[] = [{ time: "", total: 0 }];
    graphTitle: string = "Graphic";
    salesIn: string = "";
    isLoadedGraphic: boolean = false;
    graphicType?: StatsPeriodDataType;
    prevStats?: OrdersTotalPriceForCurrencyId;

    constructor() {
        makeAutoObservable(this);
        reaction(
            () => {
                return {
                    from: this.from,
                    to: this.to,
                    currentMenu: this.currentMenu,
                };
            },
            async (value, prevValue) => {
                if (
                    value.from !== prevValue.from ||
                    value.to !== prevValue.to ||
                    value.currentMenu !== prevValue.currentMenu
                ) {

                    await this.loadGeneralDetails(value.from, value.to);
                    await this.loadPrevStatDetails();

                }
            }
        );

        //this.init();
    }

    countFromToData(period: unitOfTime.DurationConstructor) {
        let from: Date;
        let to: Date;
        let milliSecInDay = 86400000;
        let milliSecInHour = 3600000;
        let milliSecInMin = 60000;

        let diffFromTo = this.to.toDate().valueOf() - this.from.toDate().valueOf();

        let days = Math.floor(diffFromTo / milliSecInDay);
        let hours = Math.floor((diffFromTo % milliSecInDay) / milliSecInHour);
        let minutes = Math.floor(
            ((diffFromTo % milliSecInDay) % milliSecInHour) / milliSecInMin
        );

        from = moment().startOf(period).subtract(1, period).toDate();
        to = moment(from)
            .add(days, "days")
            .add(hours, "hours")
            .add(minutes, "minutes")
            .toDate();
        return { from: from, to: to };
    }

    dataForPrevStat() {
        let from: Date;
        let to: Date;
        switch (this.period.periodName) {
            case Period.today: {
                from = moment().subtract(1, "day").startOf("day").toDate();
                to = moment().startOf("day").toDate();
                return { from: from, to: to, menuId: this.currentMenu.id };
            }

            case Period.yesterday: {
                from = moment().subtract(2, "day").startOf("day").toDate();
                to = moment().subtract(1, "day").startOf("day").toDate();
                return { from: from, to: to, menuId: this.currentMenu.id };
            }

            case Period.beginning_week: {
                let dates = this.countFromToData("week");
                return { from: dates.from, to: dates.to, menuId: this.currentMenu.id };
            }

            case Period.previous_week: {
                from = moment().startOf("week").subtract(2, "week").toDate();
                to = moment().startOf("week").subtract(1, "week").toDate();
                return { from: from, to: to, menuId: this.currentMenu.id };
            }

            case Period.beginning_month: {
                let dates = this.countFromToData("month");
                return { from: dates.from, to: dates.to, menuId: this.currentMenu.id };
            }

            case Period.previous_month: {
                from = moment().startOf("month").subtract(2, "month").toDate();
                to = moment().startOf("month").subtract(1, "month").toDate();
                return { from: from, to: to, menuId: this.currentMenu.id };
            }

            case Period.beginning_quarter: {
                let dates = this.countFromToData("quarter");
                return { from: dates.from, to: dates.to, menuId: this.currentMenu.id };
            }

            case Period.previous_quarter: {
                from = moment().startOf("quarter").subtract(2, "quarter").toDate();
                to = moment().startOf("quarter").subtract(1, "quarter").toDate();
                return { from: from, to: to, menuId: this.currentMenu.id };
            }

            case Period.beginning_year: {
                from = moment().startOf("year").subtract(1, "year").toDate();
                to = moment().startOf("year").toDate();
                return { from: from, to: to, menuId: this.currentMenu.id };
            }

            case Period.previous_year: {
                from = moment().startOf("year").subtract(2, "year").toDate();
                to = moment().startOf("year").subtract(1, "year").toDate();
                return { from: from, to: to, menuId: this.currentMenu.id };
            }

            case Period.custom_period: {
                let milliSecInDay = 86400000;
                let milliSecInHour = 3600000;
                let milliSecInMin = 60000;

                let diffFromTo =
                    this.to.toDate().valueOf() - this.from.toDate().valueOf();

                let days = Math.floor(diffFromTo / milliSecInDay);
                let hours = Math.floor((diffFromTo % milliSecInDay) / milliSecInHour);
                let minutes = Math.floor(
                    ((diffFromTo % milliSecInDay) % milliSecInHour) / milliSecInMin
                );

                let bufFrom = moment(Object.assign({}, this.from));
                from = bufFrom
                    .subtract(days, "days")
                    .subtract(hours, "hours")
                    .subtract(minutes, "minutes")
                    .toDate();
                to = this.from.toDate();
                return { from: from, to: to, menuId: this.currentMenu.id };
            }

            default: {
                return { from: undefined, to: undefined, menuId: this.currentMenu.id };
            }
        }
    }

    async loadPrevStatDetails() {
        if (!this.currentMenu) return;
        let dataForPrev = this.dataForPrevStat();

        const prevStats = await getStats({
            from: dataForPrev.from,
            to: dataForPrev.to,
            menuId: this.currentMenu.id,
        });

        runInAction(() => {
            if (prevStats.ordersTotalPrices.length > 0) {
                this.prevStats = prevStats.ordersTotalPrices[0];
            } else {
                this.prevStats = undefined;
            }
        });
    }

    async loadGeneralDetails(from: Moment, to: Moment) {
        if (!this.currentMenu) return;
        const generalStats = await getStats({
            from: from.toDate(),
            to: to.toDate(),
            menuId: this.currentMenu.id,
        });

        runInAction(() => {
            if (generalStats.ordersTotalPrices.length > 0) {
                this.generalStats = generalStats.ordersTotalPrices[0];
            } else {
                this.generalStats = undefined;
            }
        });
    }

    async init() {
        const menusResponse = await getMenus();
        runInAction(() => {
            this.menusList = menusResponse.menus;
            if (this.menusList.length !== 0) {
                this.currentMenu = this.menusList[0];
            }
        });
    }

    @action
    setTimeInterval(from: Moment, to: Moment) {
        this.from = from;
        this.to = to;
    }

    @action
    setMenu(menuId: number) {
        this.currentMenu = this.menusList.find((item) => item.id === menuId);
    }

    @action
    setPeriodOption(period: IPeriodOption) {
        this.period = period;
    }

    @action
    setPeriodName(periodName: Period) {
        this.period.periodName = periodName;
    }

    @computed
    getTitle(t: any) {
        if (this.mode === SelectMode.template) {
            return t(`stats:${this.period.title}`);
        } else {
            return `${this.from.format("DD.MM.YYYY HH:mm")} - ${this.to.format(
                "DD.MM.YYYY HH:mm"
            )}`;
        }
    }

    @action
    setMode(mode: SelectMode) {
        this.mode = mode;
    }

    @action
    setDataForGraph(data: DataForGraph[]) {
        this.dataForGraph = data;
    }

    @action
    setGraphTitle(title: string) {
        this.graphTitle = title;
    }

    @action
    setSalesIn(salesIn: string) {
        this.salesIn = salesIn;
    }

    @action
    setIsLoadedGraphic(isLoaded: boolean) {
        this.isLoadedGraphic = isLoaded;
    }

    @action
    setGraphicType(graphicType: StatsPeriodDataType) {
        this.graphicType = graphicType;
    }
}

export default PageStore;
